import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import AuthButton from 'app/pages/Auth/components/AuthButton';
import AuthField from 'app/pages/Auth/components/AuthField';
import AuthHeading from 'app/pages/Auth/components/AuthHeading';

import { useRequestPasswordReset } from '../hooks/useRequestPasswordReset';

const schema = yup
  .object({
    email: yup
      .string()
      .trim()
      .lowercase()
      .email('You must provide a valid email address')
      .required('Email is required'),
  })
  .required();

type ForgotPasswordFormValues = yup.InferType<typeof schema>;

function ForgotPasswordForm(): ReactElement {
  const navigate = useNavigate();
  const { mutate: requestReset, isPending, isSuccess } = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({ resolver: yupResolver(schema) });

  function onSubmit(data: ForgotPasswordFormValues): void {
    if (isPending) return;

    requestReset(data.email);
  }

  if (isSuccess) {
    return (
      <>
        <AuthHeading
          title="Check your email"
          description="If an account exists for that email, we've sent a link to reset your password."
        />
        <AuthButton type="button" onClick={() => navigate('/login')}>
          Back to login
        </AuthButton>
      </>
    );
  }

  return (
    <>
      <AuthHeading
        title="Reset your password"
        description="Enter your email address and we'll send you a link to reset your password."
        back={{ to: '/login', label: 'Back to login' }}
      />
      <form
        className="vtwo:flex vtwo:flex-col vtwo:gap-6"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <AuthField
          label="Email"
          type="email"
          autoComplete="username"
          error={errors.email?.message}
          {...register('email')}
        />
        <AuthButton type="submit" isLoading={isPending}>
          Send reset link
        </AuthButton>
      </form>
    </>
  );
}

export default ForgotPasswordForm;
